import type { ColorMode } from './imageProcessing';

// Camera + auto-capture helpers. The pure pieces (constraints, stability tracking) are unit-tested;
// permission/stream access is browser-only.

export function cameraConstraints(facing: 'environment' | 'user' = 'environment'): MediaStreamConstraints {
  return { video: { facingMode: { ideal: facing }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false };
}

/** Decide whether the frame is still enough to auto-capture. Pure + tested. */
export function shouldAutoCapture(diff: number, threshold: number, stableFrames: number, requiredFrames: number): boolean {
  return diff <= threshold && stableFrames >= requiredFrames;
}

/**
 * Tracks frame-to-frame stability and signals a one-shot auto-capture once the view has been steady
 * for the required number of frames. Deterministic and unit-tested.
 */
export class AutoCaptureTracker {
  private stable = 0;
  private fired = false;
  constructor(private threshold = 0.02, private requiredFrames = 8) {}

  /** Returns true exactly once, when stability is first reached. */
  push(diff: number): boolean {
    if (this.fired) return false;
    if (diff <= this.threshold) this.stable += 1; else this.stable = 0;
    if (shouldAutoCapture(diff, this.threshold, this.stable, this.requiredFrames)) { this.fired = true; return true; }
    return false;
  }
  get stableFrames(): number { return this.stable; }
  reset(): void { this.stable = 0; this.fired = false; }
}

export interface CameraPermissionState { state: 'granted' | 'denied' | 'prompt' | 'unknown' }

export async function queryCameraPermission(): Promise<CameraPermissionState> {
  try {
    if (!('permissions' in navigator)) return { state: 'unknown' };
    const status = await (navigator.permissions as Permissions).query({ name: 'camera' as PermissionName });
    return { state: status.state as CameraPermissionState['state'] };
  } catch {
    return { state: 'unknown' };
  }
}

export function stopStream(stream: MediaStream | null | undefined): void {
  stream?.getTracks().forEach(track => track.stop());
}

export const COLOR_MODES: ColorMode[] = ['color', 'grayscale', 'blackwhite'];
